import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBadge,
  IonButton,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonDatetime,
  IonAlert,
  useIonToast,
  IonFooter,
} from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isSameDay, parseISO, format as formatDate } from 'date-fns';
import { OrdersSummaryMetrics, OrderWithDetails } from 'shared';
import { useHistory } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import apiClient from '../api/client';
import { formatCurrency, formatDateTime } from '../utils/format';
import { RefresherEvent } from '../types/ionic';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useLanguage } from '../context/LanguageContext';

type OrdersTab = 'all' | 'today' | 'paid' | 'unpaid';

const Orders: React.FC = () => {
  const [activeTab, setActiveTab] = useState<OrdersTab>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const queryClient = useQueryClient();
  const history = useHistory();
  const [presentToast] = useIonToast();

  const ordersQuery = useQuery({
    queryKey: ['orders', 'list'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        orders: OrderWithDetails[];
        total: number;
      }>('/orders', { params: { page: 1, limit: 50 } });
      return data.orders;
    },
  });

  const normalizedSearch = searchTerm.trim();
  const summaryQuery = useQuery({
    queryKey: ['orders', 'summary', activeTab, normalizedSearch, selectedDate],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (activeTab !== 'all') {
        params.tab = activeTab;
      }
      if (selectedDate) {
        params.date = selectedDate;
      }
      if (normalizedSearch) {
        params.customer = normalizedSearch;
      }
      const { data } = await apiClient.get<OrdersSummaryMetrics>(
        '/analytics/orders/summary',
        { params },
      );
      return data;
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await Promise.all([ordersQuery.refetch(), summaryQuery.refetch()]);
    } finally {
      event.detail.complete();
    }
  };

  const ordersById = useMemo(() => {
    const map = new Map<string, OrderWithDetails>();
    (ordersQuery.data ?? []).forEach((order) => {
      map.set(order.id, order);
    });
    return map;
  }, [ordersQuery.data]);

  const filteredOrders = useMemo(() => {
    const orders = ordersQuery.data ?? [];
    const loweredSearch = normalizedSearch.toLowerCase();
    return orders
      .filter((order) => {
        if (!loweredSearch) {
          return true;
        }
        const customerName = order.customer?.name?.toLowerCase() ?? '';
        const refId = order.refId?.toLowerCase() ?? '';
        return customerName.includes(loweredSearch) || refId.includes(loweredSearch);
      })
      .filter((order) => {
        if (!selectedDate) {
          return true;
        }
        const dateValue =
          typeof order.orderDate === 'string' ? parseISO(order.orderDate) : order.orderDate;
        if (!dateValue) {
          return false;
        }
        const filterDate = parseISO(selectedDate);
        return isSameDay(dateValue, filterDate);
      })
      .filter((order) => {
        if (activeTab === 'all') {
          return true;
        }
        if (activeTab === 'paid') {
          return order.isPaid;
        }
        if (activeTab === 'unpaid') {
          return !order.isPaid;
        }
        // today tab
        const dateValue =
          typeof order.orderDate === 'string' ? parseISO(order.orderDate) : order.orderDate;
        if (!dateValue) {
          return false;
        }
        return isSameDay(dateValue, new Date());
      });
  }, [activeTab, ordersQuery.data, normalizedSearch, selectedDate]);

  const summaryCards = useMemo(() => {
    const summary = summaryQuery.data;
    return [
      {
        id: 'orders',
        title: activeTab === 'today' ? t('todayOrders') : t('totalOrders'),
        value: `${activeTab === 'today' ? summary?.todayOrders ?? 0 : summary?.totalOrders ?? 0}`,
        context:
          activeTab === 'today'
            ? `${summary?.totalOrders ?? 0} ${t('totalOrders').toLowerCase()}`
            : `${summary?.todayOrders ?? 0} ${t('today').toLowerCase()}`,
      },
      {
        id: 'revenue',
        title: t('revenue'),
        value: formatCurrency(
          activeTab === 'today'
            ? summary?.todayRevenue ?? 0
            : summary?.totalRevenue ?? 0,
        ),
        context:
          activeTab === 'today'
            ? `${t('totalRevenue')}: ${formatCurrency(summary?.totalRevenue ?? 0)}`
            : `${summary?.todayOrders ?? 0} ${t('orders').toLowerCase()} ${t('today').toLowerCase()}`,
      },
      {
        id: 'profit',
        title: t('profit'),
        value: formatCurrency(
          activeTab === 'today'
            ? summary?.todayProfit ?? 0
            : summary?.totalProfit ?? 0,
        ),
        context: `${summary?.unpaidOrders ?? 0} ${t('unpaid').toLowerCase()} ${t('orders').toLowerCase()}`,
      },
    ];
  }, [activeTab, summaryQuery.data, t]);

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedOrderIds([]);
      }
      return !prev;
    });
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId],
    );
  };

  const clearSelection = () => {
    setSelectedOrderIds([]);
    setSelectionMode(false);
  };

  useEffect(() => {
    if (!selectionMode) {
      return;
    }
    const availableIds = new Set((ordersQuery.data ?? []).map((order) => order.id));
    setSelectedOrderIds((current) => current.filter((id) => availableIds.has(id)));
  }, [selectionMode, ordersQuery.data]);

  const markSelectedMutation = useMutation({
    mutationFn: async ({ orderIds, action }: { orderIds: string[]; action: 'paid' | 'unpaid' }) => {
      await Promise.all(
        orderIds.map((orderId) =>
          action === 'paid'
            ? apiClient.patch(`/orders/${orderId}/mark-paid`)
            : apiClient.patch(`/orders/${orderId}/mark-unpaid`),
        ),
      );
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        ordersQuery.refetch(),
        summaryQuery.refetch(),
      ]);
      presentToast({
        message:
          variables.action === 'paid'
            ? t('ordersMarkedAsPaidBulk') ?? 'Orders marked as paid'
            : t('ordersMarkedAsUnpaidBulk') ?? 'Orders marked as unpaid',
        duration: 2000,
        color: 'success',
      });
      clearSelection();
    },
    onError: () => {
      presentToast({
        message: t('orderUpdateFailed') ?? 'Failed to update orders',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteSelectedMutation = useMutation({
    mutationFn: async (orderIds: string[]) => {
      if (orderIds.length === 1) {
        await apiClient.delete(`/orders/${orderIds[0]}`);
      } else {
        await apiClient.delete('/orders/bulk', { data: { ids: orderIds } });
      }
    },
    onSuccess: async () => {
      await Promise.all([
        ordersQuery.refetch(),
        summaryQuery.refetch(),
      ]);
      presentToast({
        message: t('ordersDeleted') ?? 'Orders deleted',
        duration: 2000,
        color: 'success',
      });
      clearSelection();
    },
    onError: () => {
      presentToast({
        message: t('ordersDeleteFailed') ?? 'Failed to delete orders',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const hasSelection = selectedOrderIds.length > 0;
  const isBulkActionInFlight =
    markSelectedMutation.isPending || deleteSelectedMutation.isPending;

  const handleBulkStatusChange = (action: 'paid' | 'unpaid') => {
    if (!hasSelection || isBulkActionInFlight) {
      return;
    }
    markSelectedMutation.mutate({ orderIds: selectedOrderIds, action });
  };

  const handleBulkDelete = () => {
    if (!hasSelection || isBulkActionInFlight) {
      return;
    }
    deleteSelectedMutation.mutate(selectedOrderIds);
  };

  const getOrdersForIds = (ids: string[]) =>
    ids
      .map((orderId) => ordersById.get(orderId))
      .filter((order): order is OrderWithDetails => Boolean(order));

  const generateOrderTableRows = (order: OrderWithDetails) => {
    if (!Array.isArray(order.items) || order.items.length === 0) {
      return '';
    }
    return order.items
      .map((item) => {
        const name = item.menuItem?.product?.name || t('unknown');
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unitPrice || 0);
        const subtotal = Number(item.subtotal || unitPrice * quantity);
        return `
          <tr>
            <td>${name}</td>
            <td>${quantity}</td>
            <td>${formatCurrency(unitPrice, locale)}</td>
            <td>${formatCurrency(subtotal, locale)}</td>
          </tr>
        `;
      })
      .join('');
  };

  const openPrintWindow = (title: string, content: string) => {
    const printWindow = window.open('', '', 'height=700,width=480');
    if (!printWindow) {
      presentToast({
        message: t('printUnavailable') ?? 'Unable to open print view',
        duration: 2000,
        color: 'danger',
      });
      return;
    }
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #222; }
            h1, h2, h3 { margin: 0 0 12px; }
            table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; font-size: 14px; }
            th { background: #f7f7f7; }
            .order-header { margin: 24px 0 8px; padding-bottom: 8px; border-bottom: 2px solid #333; }
            .meta { color: #666; margin-bottom: 4px; font-size: 13px; }
            .status { margin-top: 4px; font-weight: 600; }
            .total { font-size: 16px; font-weight: 700; margin-top: 12px; text-align: right; }
            .grand-total { font-size: 18px; font-weight: 700; margin-top: 24px; text-align: right; }
            button { display: none; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          ${content}
          <button onclick="window.print()">${t('print') ?? 'Print'}</button>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const buildSingleOrderPrint = (order: OrderWithDetails) => `
    <div>
      <h2>☕ Cafe</h2>
      <div class="meta"><strong>${t('orderSummary') ?? 'Order Summary'}</strong></div>
      <div class="meta">${t('orderDate') ?? 'Date'}: ${formatDateTime(order.orderDate)}</div>
      <div class="meta">${t('customer') ?? 'Customer'}: ${order.customer?.name || t('walkInCustomer') || 'Walk-in customer'}</div>
      <div class="status">
        ${t('status') ?? 'Status'}: ${order.isPaid ? t('paid') ?? 'Paid' : t('unpaid') ?? 'Unpaid'}
        ${order.paymentMethod ? `(${order.paymentMethod.toUpperCase()})` : ''}
      </div>
      <table>
        <thead>
          <tr>
            <th>${t('itemsSold') ?? 'Items'}</th>
            <th>${t('quantity') ?? 'Qty'}</th>
            <th>${t('price') ?? 'Price'}</th>
            <th>${t('totalAmount') ?? 'Total'}</th>
          </tr>
        </thead>
        <tbody>
          ${generateOrderTableRows(order)}
        </tbody>
      </table>
      <div class="total">
        ${t('totalAmount') ?? 'Total'}: ${formatCurrency(Number(order.totalAmount || 0), locale)}
      </div>
    </div>
  `;

  const buildCombinedOrdersPrint = (orders: OrderWithDetails[]) => {
    const customerName = orders[0]?.customer?.name || t('walkInCustomer') || 'Walk-in customer';
    const grandTotal = orders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
    const orderSections = orders
      .map((order) => {
        const total = Number(order.totalAmount || 0);
        return `
          <div class="order-block">
            <div class="order-header">
              <h3>${formatDateTime(order.orderDate)}</h3>
              <div class="meta">${t('orders') ?? 'Order'} #${order.refId || order.id}</div>
              <div class="status">
                ${t('status') ?? 'Status'}: ${order.isPaid ? t('paid') ?? 'Paid' : t('unpaid') ?? 'Unpaid'}
                ${order.paymentMethod ? `(${order.paymentMethod.toUpperCase()})` : ''}
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>${t('itemsSold') ?? 'Items'}</th>
                  <th>${t('quantity') ?? 'Qty'}</th>
                  <th>${t('price') ?? 'Price'}</th>
                  <th>${t('totalAmount') ?? 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                ${generateOrderTableRows(order)}
              </tbody>
            </table>
            <div class="total">${t('totalAmount') ?? 'Total'}: ${formatCurrency(total, locale)}</div>
          </div>
        `;
      })
      .join('');

    return `
      <div>
        <h2>☕ Cafe</h2>
        <div class="meta"><strong>${t('customer') ?? 'Customer'}:</strong> ${customerName}</div>
        <div class="meta"><strong>${t('printDate') ?? 'Print date'}:</strong> ${formatDateTime(new Date().toISOString())}</div>
        ${orderSections}
        <div class="grand-total">${t('totalAmount') ?? 'Total'}: ${formatCurrency(grandTotal, locale)}</div>
      </div>
    `;
  };

  const handlePrintOrders = (orderIds: string[]) => {
    const orders = orderIds.length ? getOrdersForIds(orderIds) : [];
    if (orders.length === 0) {
      presentToast({
        message: t('noOrderData') ?? 'No order data available',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    const content =
      orders.length === 1
        ? buildSingleOrderPrint(orders[0])
        : buildCombinedOrdersPrint(orders);
    openPrintWindow(t('printOrders') ?? 'Print orders', content);
  };

  const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeCsvValue = (value: string | number | null | undefined) => {
    const normalized = value ?? '';
    const stringValue = typeof normalized === 'string' ? normalized : String(normalized);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const handleExportOrders = (orderIds: string[]) => {
    const orders = orderIds.length ? getOrdersForIds(orderIds) : [];
    if (orders.length === 0) {
      presentToast({
        message: t('noOrderData') ?? 'No order data available',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    const header = [
      'Order ID',
      t('customer') ?? 'Customer',
      t('orderDate') ?? 'Date',
      t('totalAmount') ?? 'Total',
      t('status') ?? 'Status',
      t('itemsSold') ?? 'Items',
    ];

    const rows = orders.map((order) => {
      const itemsSummary = Array.isArray(order.items)
        ? order.items
            .map((item) => {
              const name = item.menuItem?.product?.name || t('unknown');
              const qty = Number(item.quantity || 0);
              return `${name} × ${qty}`;
            })
            .join('; ')
        : '';

      return [
        order.refId || order.id,
        order.customer?.name || t('walkInCustomer') || 'Walk-in customer',
        formatDateTime(order.orderDate),
        formatCurrency(Number(order.totalAmount || 0), locale),
        order.isPaid ? t('paid') ?? 'Paid' : t('unpaid') ?? 'Unpaid',
        itemsSummary,
      ];
    });

    const csvContent = [header, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\r\n');

    const timestamp = new Date().toISOString().split('T')[0];
    downloadCsv(`orders-${timestamp}.csv`, csvContent);

    presentToast({
      message: t('ordersExported') ?? 'Orders exported',
      duration: 2000,
      color: 'success',
    });
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('orders')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <IonButton expand="block" style={{ marginBottom: 16 }} onClick={() => history.push('/tabs/orders/new')}>
            {t('addOrder') ?? 'Add order'}
          </IonButton>
          <IonSegment
            value={activeTab}
            onIonChange={(event) =>
              setActiveTab((event.detail.value as OrdersTab) || 'all')
            }
          >
            <IonSegmentButton value="all">
              <IonLabel>{t('all')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="today">
              <IonLabel>{t('today')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="paid">
              <IonLabel>{t('paid')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="unpaid">
              <IonLabel>{t('unpaid')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          <div style={{ marginTop: 16 }}>
            <IonSearchbar
              value={searchTerm}
              onIonChange={(event) => setSearchTerm(event.detail.value ?? '')}
              placeholder={t('search')}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <IonButton
                size="small"
                fill="outline"
                onClick={() => setShowDatePicker(true)}
              >
                {selectedDate
                  ? formatDate(parseISO(selectedDate), 'dd MMM yyyy')
                  : t('date')}
              </IonButton>
              {selectedDate ? (
                <IonButton
                  size="small"
                  fill="clear"
                  onClick={() => setSelectedDate(null)}
                >
                  {t('clear')}
                </IonButton>
              ) : null}
              <IonButton
                size="small"
                fill={selectionMode ? 'solid' : 'outline'}
                color={selectionMode ? 'primary' : 'medium'}
                onClick={toggleSelectionMode}
              >
                {selectionMode ? t('cancelSelection') ?? 'Done' : t('selectOrders') ?? 'Select orders'}
              </IonButton>
            </div>
          </div>

          <section style={{ marginTop: 24 }}>
            {summaryQuery.isLoading && !summaryQuery.data ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <IonSpinner />
              </div>
            ) : (
              summaryCards.map((card) => (
                <MetricCard
                  key={card.id}
                  title={card.title}
                  value={card.value}
                  context={card.context}
                  accent={card.id === 'orders' ? 'primary' : card.id === 'revenue' ? 'success' : 'warning'}
                />
              ))
            )}
          </section>

          <section style={{ marginTop: 24, marginBottom: 24 }}>
            <IonText color="primary">
              <h2 style={{ marginBottom: 12 }}>
                {activeTab === 'all'
                  ? 'Recent Orders'
                  : `Orders – ${activeTab.toUpperCase()}`}
              </h2>
            </IonText>
            {ordersQuery.isLoading && !ordersQuery.data ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                <IonSpinner />
              </div>
            ) : filteredOrders.length === 0 ? (
              <IonText color="medium">
                <p>{t('noOrdersFound')}</p>
              </IonText>
            ) : (
              <IonList>
                {filteredOrders.map((order) => {
                  const isSelected = selectedOrderIds.includes(order.id);
                  const itemStyle =
                    selectionMode && isSelected
                      ? {
                          '--background': 'var(--ion-color-light)',
                          '--background-activated': 'var(--ion-color-light-shade)',
                        }
                      : undefined;

                  const handleItemClick = () => {
                    if (selectionMode) {
                      toggleOrderSelection(order.id);
                    } else {
                      setSelectedOrderId(order.id);
                    }
                  };

                  return (
                    <IonItem
                      key={order.id}
                      button={!selectionMode}
                      detail={!selectionMode}
                      onClick={handleItemClick}
                      style={itemStyle}
                    >
                      {selectionMode ? (
                        <IonCheckbox
                          slot="start"
                          checked={isSelected}
                          disabled={isBulkActionInFlight}
                          onIonChange={(event) => {
                            event.stopPropagation();
                            toggleOrderSelection(order.id);
                          }}
                          aria-label="Select order"
                        />
                      ) : null}
                      <IonLabel>
                        <h3 style={{ fontWeight: 600 }}>
                          {order.customer?.name || 'Walk-in customer'}
                        </h3>
                        <p style={{ marginTop: 4 }}>
                          {formatDateTime(order.orderDate)} •{' '}
                          {order.items?.length ?? 0} items
                        </p>
                        <p style={{ marginTop: 4 }}>
                          Total {formatCurrency(Number(order.totalAmount))}
                        </p>
                      </IonLabel>
                      <IonBadge
                        color={order.isPaid ? 'success' : 'warning'}
                        slot="end"
                      >
                        {order.isPaid ? 'Paid' : 'Unpaid'}
                      </IonBadge>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </section>
        </div>
      </IonContent>

      {selectionMode ? (
        <IonFooter>
          <IonToolbar>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              <IonText>
                <strong>{selectedOrderIds.length}</strong>{' '}
                {t('selected') ?? 'selected'}
              </IonText>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginLeft: 'auto',
                  flexWrap: 'wrap',
                }}
              >
                <IonButton
                  size="small"
                  fill="outline"
                  color="medium"
                  disabled={!hasSelection || isBulkActionInFlight}
                  onClick={() => handlePrintOrders(selectedOrderIds)}
                >
                  {t('print') ?? 'Print'}
                </IonButton>
                <IonButton
                  size="small"
                  fill="outline"
                  color="tertiary"
                  disabled={!hasSelection || isBulkActionInFlight}
                  onClick={() => handleExportOrders(selectedOrderIds)}
                >
                  {t('exportCsv') ?? 'Export CSV'}
                </IonButton>
                <IonButton
                  size="small"
                  color="success"
                  disabled={!hasSelection || isBulkActionInFlight}
                  onClick={() => handleBulkStatusChange('paid')}
                >
                  {t('markAsPaid') ?? 'Mark paid'}
                </IonButton>
                <IonButton
                  size="small"
                  color="warning"
                  disabled={!hasSelection || isBulkActionInFlight}
                  onClick={() => handleBulkStatusChange('unpaid')}
                >
                  {t('markAsUnpaid') ?? 'Mark unpaid'}
                </IonButton>
                <IonButton
                  size="small"
                  color="danger"
                  disabled={!hasSelection || isBulkActionInFlight}
                  onClick={handleBulkDelete}
                >
                  {t('delete')}
                </IonButton>
              </div>
            </div>
          </IonToolbar>
        </IonFooter>
      ) : null}

      <IonModal
        isOpen={Boolean(selectedOrderId)}
        onDidDismiss={() => setSelectedOrderId(null)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('orderSummary')}</IonTitle>
            <IonButton slot="end" fill="clear" onClick={() => setSelectedOrderId(null)}>
              {t('close')}
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <OrderDetailModalContent
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onStatusChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
            summaryQuery.refetch();
          }}
          onPrintOrder={(orderId) => handlePrintOrders([orderId])}
        />
      </IonModal>

      <IonModal
        isOpen={showDatePicker}
        onDidDismiss={() => setShowDatePicker(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('date')}</IonTitle>
            <IonButton slot="end" fill="clear" onClick={() => setShowDatePicker(false)}>
              {t('close')}
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonDatetime
            presentation="date"
            value={selectedDate ?? undefined}
            onIonChange={(event) => {
              const value = event.detail.value;
              if (value) {
                const normalized = Array.isArray(value) ? value[0] : value;
                setSelectedDate(normalized);
              } else {
                setSelectedDate(null);
              }
              setShowDatePicker(false);
            }}
          />
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

interface OrderDetailModalContentProps {
  orderId: string | null;
  onClose: () => void;
  onStatusChanged: () => Promise<void> | void;
  onPrintOrder: (orderId: string) => void;
}

const OrderDetailModalContent: React.FC<OrderDetailModalContentProps> = ({
  orderId,
  onClose,
  onStatusChanged,
  onPrintOrder,
}) => {
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const [presentToast] = useIonToast();
  const queryClient = useQueryClient();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const history = useHistory();

  const detailQuery = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: async () => {
      const { data } = await apiClient.get<OrderWithDetails>(`/orders/${orderId}`);
      return data;
    },
    enabled: Boolean(orderId),
  });

  const markPaidMutation = useMutation({
    mutationFn: async () => {
      if (!orderId) return;
      await apiClient.patch(`/orders/${orderId}/mark-paid`);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'detail', orderId] }),
        onStatusChanged(),
      ]);
      presentToast({
        message: t('orderMarkedAsPaid') ?? 'Order marked as paid',
        duration: 2000,
        color: 'success',
      });
    },
    onError: () => {
      presentToast({
        message: t('failedToUpdate') ?? 'Failed to update order',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const markUnpaidMutation = useMutation({
    mutationFn: async () => {
      if (!orderId) return;
      await apiClient.patch(`/orders/${orderId}/mark-unpaid`);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'detail', orderId] }),
        onStatusChanged(),
      ]);
      presentToast({
        message: t('orderMarkedAsUnpaid') ?? 'Order marked as unpaid',
        duration: 2000,
        color: 'success',
      });
    },
    onError: () => {
      presentToast({
        message: t('failedToUpdate') ?? 'Failed to update order',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteOrderMutation = useMutation({
    mutationFn: async () => {
      if (!orderId) return;
      await apiClient.delete(`/orders/${orderId}`);
    },
    onSuccess: async () => {
      await onStatusChanged();
      queryClient.invalidateQueries({ queryKey: ['orders', 'detail', orderId] });
      presentToast({
        message: t('orderDeleted') ?? 'Order deleted',
        duration: 2000,
        color: 'success',
      });
      onClose();
    },
    onError: () => {
      presentToast({
        message: t('orderDeleteFailed') ?? 'Failed to delete order',
        duration: 2000,
        color: 'danger',
      });
    },
    onSettled: () => {
      setShowDeleteAlert(false);
    },
  });

  if (!orderId) {
    return null;
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <IonContent className="ion-padding">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <IonSpinner />
        </div>
      </IonContent>
    );
  }

  const order = detailQuery.data;
  const totalAmount = formatCurrency(Number(order.totalAmount || 0), locale);
  const paidLabel = order.isPaid ? t('paid') : t('unpaid');

  return (
    <IonContent className="ion-padding">
      <section style={{ marginBottom: 16 }}>
        <IonText>
          <h2 style={{ marginBottom: 4 }}>{order.customer?.name || 'Walk-in customer'}</h2>
        </IonText>
        <IonButton
          size="small"
          fill="outline"
          style={{ marginBottom: 8 }}
          onClick={() => {
            history.push('/tabs/customers', {
              customerId: order.customer?.id,
            });
            onClose();
          }}
        >
          {t('viewCustomers') ?? t('customers')}
        </IonButton>
        <IonText color="medium">
          <p>{formatDateTime(order.orderDate)}</p>
        </IonText>
        <IonText color={order.isPaid ? 'success' : 'warning'}>
          <p>{paidLabel}</p>
        </IonText>
      </section>

      <section style={{ marginBottom: 16 }}>
        <IonText>
          <h3>{t('orderSummary')}</h3>
        </IonText>
        <IonList>
          <IonItem lines="none">
            <IonLabel>
              <p>{t('totalAmount')}</p>
              <h3>{totalAmount}</h3>
            </IonLabel>
          </IonItem>
          <IonItem lines="none">
            <IonLabel>
              <p>{t('status')}</p>
              <h3>{paidLabel}</h3>
            </IonLabel>
          </IonItem>
        </IonList>
      </section>

      <section style={{ marginBottom: 16 }}>
        <IonText>
          <h3>{t('itemsSold')}</h3>
        </IonText>
        <IonList>
          {(order.items ?? []).map((item) => (
            <IonItem key={item.id}>
              <IonLabel>
                <h3>{item.menuItem?.product?.name || t('unknown')}</h3>
                <p>
                  {item.quantity} × {formatCurrency(Number(item.unitPrice || 0), locale)}
                </p>
              </IonLabel>
              <IonBadge color="primary" slot="end">
                {formatCurrency(Number(item.subtotal || 0), locale)}
              </IonBadge>
            </IonItem>
          ))}
        </IonList>
      </section>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <IonButton
          expand="block"
          fill="outline"
          color="medium"
          onClick={() => {
            if (orderId) {
              onPrintOrder(orderId);
            }
          }}
        >
          {t('print') ?? 'Print'}
        </IonButton>
        <IonButton expand="block" fill="outline" onClick={onClose}>
          {t('close')}
        </IonButton>
        {order.isPaid ? (
          <IonButton
            expand="block"
            color="warning"
            onClick={() => markUnpaidMutation.mutate()}
            disabled={markUnpaidMutation.isPending}
          >
            {t('markAsUnpaid') ?? 'Mark unpaid'}
          </IonButton>
        ) : (
          <IonButton
            expand="block"
            color="success"
            onClick={() => markPaidMutation.mutate()}
            disabled={markPaidMutation.isPending}
          >
            {t('markAsPaid') ?? 'Mark paid'}
          </IonButton>
        )}
        <IonButton
          expand="block"
          color="danger"
          onClick={() => setShowDeleteAlert(true)}
          disabled={deleteOrderMutation.isPending}
        >
          {t('delete')}
        </IonButton>
      </div>

      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header={t('deleteOrder') ?? 'Delete order'}
        message={t('confirmDeleteOrder') ?? 'Are you sure you want to delete this order?'}
        buttons={[
          {
            text: t('cancel'),
            role: 'cancel',
            handler: () => setShowDeleteAlert(false),
          },
          {
            text: t('delete'),
            role: 'destructive',
            handler: () => deleteOrderMutation.mutate(),
          },
        ]}
      />
    </IonContent>
  );
};

export default Orders;
